import BaseApiClient from '../BaseApiClient';
import type { PaginatedResponse } from '../types';
import {
  MeetingImportStates,
  MeetingListItem,
  MeetingSource
} from '@/components/page-components/meetings/types';
import { normalizeMeetingDetailInsights } from '@/components/page-components/meetings/normalizeMeetingInsights';
import { MEETINGS_API_VERSION, meetingsApiPaths } from './paths';
import type {
  MeetingBundle,
  MeetingListParams,
  UpdateMeetingSpeakersPayload
} from './types';

export type { PaginatedResponse } from '../types';
export type {
  MeetingBundle,
  MeetingListParams,
  UpdateMeetingSpeakersPayload
} from './types';

/** Map alternate API field names onto MeetingListItem.source (RDNEX-374). */
function normalizeMeetingListItem(raw: unknown): MeetingListItem {
  const item = raw as MeetingListItem & {
    meeting_source?: string;
    call_source?: string;
  };
  const source = (item.source ??
    item.meeting_source ??
    item.call_source ??
    '') as MeetingSource;
  return { ...item, source };
}

function normalizeMeetingBundle(payload: {
  meeting: MeetingListItem;
  detail?: unknown;
  analysis?: unknown;
}): MeetingBundle {
  const detailRaw = (payload.detail ?? payload.analysis) as Parameters<
    typeof normalizeMeetingDetailInsights
  >[0];
  return {
    meeting: payload.meeting,
    detail: normalizeMeetingDetailInsights(detailRaw)
  };
}
/**
 * Meetings / Call details API — FlightDeck HTTP only (no FE fixtures).
 */
export class ApiMeetings extends BaseApiClient {
  private withVersionHeader() {
    return { headers: { Version: MEETINGS_API_VERSION } };
  }

  async list(
    params?: MeetingListParams
  ): Promise<PaginatedResponse<MeetingListItem>> {
    const res = await this.client.get(meetingsApiPaths.list, {
      ...this.withVersionHeader(),
      params
    });
    const rawData = res.data;
    const items =
      rawData?.data ??
      rawData?.items ??
      (Array.isArray(rawData) ? rawData : []);
    if (!Array.isArray(items)) {
      throw new Error('Invalid meetings list response');
    }
    const fallbackPerPage =
      params?.per_page ?? (items.length > 0 ? items.length : 25);
    const meta = rawData?.meta ?? {
      current_page: params?.page ?? 1,
      last_page: 1,
      per_page: fallbackPerPage,
      total: items.length
    };
    return {
      data: items.map(normalizeMeetingListItem),
      meta: {
        current_page: Math.max(1, Number(meta.current_page ?? 1)),
        last_page: Math.max(1, Number(meta.last_page ?? 1)),
        per_page: Math.max(1, Number(meta.per_page ?? fallbackPerPage)),
        total: Math.max(0, Number(meta.total ?? items.length))
      }
    };
  }

  async get(id: string): Promise<MeetingBundle> {
    const res = await this.client.get(
      meetingsApiPaths.detail(id),
      this.withVersionHeader()
    );
    const payload = res.data?.data ?? res.data;
    if (!payload?.meeting || !(payload.detail || payload.analysis)) {
      throw new Error(`Invalid meeting detail response for ${id}`);
    }
    return normalizeMeetingBundle(payload);
  }

  async exclude(id: string): Promise<MeetingListItem> {
    return this.patchImportState(id, MeetingImportStates.Excluded);
  }

  async importMeeting(id: string): Promise<MeetingListItem> {
    return this.patchImportState(id, MeetingImportStates.Imported);
  }

  async resetExcluded(id: string): Promise<MeetingListItem> {
    return this.patchImportState(id, MeetingImportStates.ForImportReview);
  }

  async updateSpeakers(
    id: string,
    payload: UpdateMeetingSpeakersPayload
  ): Promise<MeetingBundle> {
    const res = await this.client.patch(
      meetingsApiPaths.speakers(id),
      {
        speakers: payload.speakers.map((row) => {
          const assignedUserId =
            row.assigned_user_id ??
            (typeof row.assigned_user?.id === 'number'
              ? row.assigned_user.id
              : row.assigned_user?.id != null
              ? Number(row.assigned_user.id) || null
              : null);

          const assignedIndividualId = assignedUserId
            ? null
            : row.assigned_individual_id ??
              (typeof row.assigned_contact?.id === 'number'
                ? row.assigned_contact.id
                : row.assigned_contact?.id != null
                ? Number(row.assigned_contact.id) || null
                : null);

          return {
            id: row.id,
            assigned_individual_id: assignedIndividualId,
            assigned_user_id: assignedUserId,
            display_name: row.display_name,
            speaker_code: row.speaker_code,
            affiliation: assignedUserId ? 'internal' : row.affiliation
          };
        })
      },
      this.withVersionHeader()
    );
    const body = res.data?.data ?? res.data;
    if (!body?.meeting || !(body.detail || body.analysis)) {
      throw new Error(`Invalid speakers update response for ${id}`);
    }
    return normalizeMeetingBundle(body);
  }

  async updateActionItem(
    meetingId: string,
    actionItemId: string,
    resolved: boolean
  ): Promise<MeetingBundle> {
    const res = await this.client.patch(
      meetingsApiPaths.actionItem(meetingId, actionItemId),
      { resolved },
      this.withVersionHeader()
    );
    const body = res.data?.data ?? res.data;
    if (!body?.meeting || !(body.detail || body.analysis)) {
      throw new Error(`Invalid action item update response for ${meetingId}`);
    }
    return normalizeMeetingBundle(body);
  }

  async linkCompany(id: string, companyId: number): Promise<MeetingBundle> {
    const res = await this.client.post(
      meetingsApiPaths.companies(id),
      { company_id: companyId },
      this.withVersionHeader()
    );
    const body = res.data?.data ?? res.data;
    if (!body?.meeting || !(body.detail || body.analysis)) {
      throw new Error(`Invalid meeting link company response for ${id}`);
    }
    return normalizeMeetingBundle(body);
  }

  async unlinkCompany(
    id: string,
    companyId: number | string
  ): Promise<MeetingBundle> {
    const res = await this.client.delete(
      meetingsApiPaths.company(id, companyId),
      this.withVersionHeader()
    );
    const body = res.data?.data ?? res.data;
    if (!body?.meeting || !(body.detail || body.analysis)) {
      throw new Error(`Invalid meeting unlink company response for ${id}`);
    }
    return normalizeMeetingBundle(body);
  }

  async updateCompany(
    id: string,
    companyId: number | null
  ): Promise<MeetingListItem> {
    const res = await this.client.patch(
      meetingsApiPaths.update(id),
      { company_id: companyId },
      this.withVersionHeader()
    );
    const payload = res.data?.data ?? res.data;
    if (!payload?.id) {
      throw new Error(`Invalid meeting update response for ${id}`);
    }
    return payload as MeetingListItem;
  }

  private async patchImportState(
    id: string,
    import_state: MeetingListItem['import_state']
  ): Promise<MeetingListItem> {
    const res = await this.client.patch(
      meetingsApiPaths.update(id),
      { import_state },
      this.withVersionHeader()
    );
    const payload = res.data?.data ?? res.data;
    if (!payload?.id) {
      throw new Error(`Invalid meeting update response for ${id}`);
    }
    return payload as MeetingListItem;
  }
}

export { meetingsApiPaths, MEETINGS_API_VERSION } from './paths';
export { meetingsQueryKeys } from './query-keys';
export { useMeetingQuery, useMeetingsListQuery } from './use-meetings-queries';
export {
  useExcludeMeetingMutation,
  useImportMeetingMutation,
  useLinkMeetingCompanyMutation,
  useResetExcludedMeetingMutation,
  useUnlinkMeetingCompanyMutation,
  useUpdateMeetingActionItemMutation,
  useUpdateMeetingCompanyMutation,
  useUpdateMeetingSpeakersMutation
} from './use-meetings-mutations';
